<script setup lang="ts">
import { computed } from "vue";
import { type Diagnostic } from "@/types";
import MapIcon from "../UI/icons/MapIcon.vue";
import DwgIcon from "../UI/icons/DwgIcon.vue";
import TemplateIcon from "../UI/icons/TemplateIcon.vue";
import { useDates } from "@/composable/useDates";
import DRDisplayItem from "../UI/items/DRDisplayItem.vue";
import ItemIndicator from "../UI/items/ItemIndicator.vue";
import type { ItemDisplayMode } from "@/stores/Settings";
import DiagnosticActionsDropdown from "./DiagnosticActionsDropdown.vue";
import { useTranslation } from "i18next-vue";

const { formatDistanceToNow } = useDates();
const { t } = useTranslation();

const props = defineProps<{
  diagnostic: Diagnostic;
  display: ItemDisplayMode;
  selected?: boolean;
}>();

const reportsCount = computed(() => props.diagnostic._count?.reports ?? 0);
const drawingsCount = computed(() => props.diagnostic._count?.drawings ?? 0);

defineEmits<{
  (e: "toggle-select"): void;
  (e: "rename", diagnostic: Diagnostic): void;
}>();
</script>

<template>
  <DRDisplayItem
    :display="display"
    class="position-relative"
    :class="{ 'is-extracting': diagnostic.isExtractingData }"
    :subtitle="
      diagnostic.isExtractingData
        ? t('report:diagnostics.extracting.data')
        : diagnostic.createdAt
        ? `${$t('Added')} ${formatDistanceToNow(diagnostic.createdAt)}`
        : ''
    "
    :selected="selected"
  >
    <template #title>
      <div class="d-flex align-items-center gap-2">
        <span v-if="diagnostic.name?.length" class="text-truncate">
          {{ diagnostic.name }}
        </span>
        <span v-else class="text-grey">
          {{ $t("report:diagnostics.item.noname") }}
        </span>
      </div>
    </template>

    <template #icon="{ size }">
      <div
        v-if="diagnostic.isExtractingData"
        class="ai-badge"
        :style="{
          width: size / 1.5 + 'px',
          height: size / 1.5 + 'px',
          margin: size / 3 + 'px',
        }"
      >
        <el-icon :size="size / 2"> <i class="bi-stars" /> </el-icon>
      </div>
      <MapIcon v-else :size="size" />
    </template>

    <!-- Ce qui est déjà rattaché à l'ouvrage, d'un coup d'œil. On réutilise les
         icônes des items rapport et plan ; rien n'est affiché si le compte est nul. -->
    <template #more>
      <span class="indicator-group">
        <ItemIndicator
          v-if="reportsCount"
          :count="reportsCount"
          :tooltip="
            t('report:diagnostics.reportsCountTooltip', { count: reportsCount })
          "
        >
          <template #icon="{ size }">
            <TemplateIcon :size="size" />
          </template>
        </ItemIndicator>

        <ItemIndicator
          v-if="drawingsCount"
          :count="drawingsCount"
          :tooltip="
            t('report:diagnostics.plansCountTooltip', { count: drawingsCount })
          "
        >
          <template #icon="{ size }">
            <DwgIcon :size="size" />
          </template>
        </ItemIndicator>
      </span>
    </template>

    <template #actions>
      <DiagnosticActionsDropdown
        :diagnostic="diagnostic"
        @toggle-select="$emit('toggle-select')"
        @rename="$emit('rename', diagnostic)"
        :selected="selected"
        show-rename
        show-inspection
        show-edit-report
      />
    </template>
  </DRDisplayItem>
</template>

<style scoped lang="scss">
@import "@/style/variables.scss";

/* Les deux compteurs se lisent comme un seul bloc */
.indicator-group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.is-extracting {
  :deep(.subtitle) {
    color: $ia !important;
  }
}

.ai-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6 0%, $ia 100%);
  color: white;
  font-size: 14px;
  animation: pulse-glow 2s infinite ease-in-out;

  i {
    animation: blink-slow 2s ease-in-out infinite;
  }
}

@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 0 rgba($ia, 0.4);
  }
  50% {
    box-shadow: 0 0 0 5px rgba($ia, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba($ia, 0);
  }
}

@keyframes blink-slow {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>
